<template>
  <section class="individual-blog-post" :style="sectionStyles">
    <div class="blog-content-wrapper">
      <!-- Full Width Hero Section -->
      <div class="hero-section">
        <div class="container">
          <header class="article-header">
            <h1 class="article-title">
              {{ shopifyData.article.title }}
            </h1>

            <!-- Article Excerpt -->
            <div v-if="excerptParts.first" class="content-section">
              <p class="intro-lead">
                {{ excerptParts.first }}
              </p>
              <p v-if="excerptParts.rest" class="intro-text">
                {{ excerptParts.rest }}
              </p>
            </div>

            <div class="article-meta">
              <time class="article-date">{{ shopifyData.article.date }}</time>
              <span v-if="shopifyData.article.author" class="article-author"
                >By {{ shopifyData.article.author }}</span
              >
            </div>
          </header>
        </div>
      </div>

      <!-- Full Width Hero Image -->
      <div class="hero-image-container">
        <img
          :src="shopifyData.article.heroImage"
          :alt="shopifyData.article.title || 'Hero Image'"
          class="hero-image"
          loading="lazy"
          aria-hidden="true"
          aria-label="Hero Image"
          width=""
          height=""
        />
      </div>
    </div>

    <!-- Main Content and Sidebar -->
    <div class="container content-container">
      <div class="blog-layout">
        <!-- Main Content Area -->
        <main class="main-content">
          <!-- Article Content -->
          <div class="article-content">
            <!-- Main Article Content -->
            <div class="content-section">
              <div class="article-body" v-html="decodedContent" />
            </div>
          </div>

          <!-- Back to Blog Link -->
          <div class="back-to-blog">
            <a :href="shopifyData.blog.url" class="back-link"
              >&lt; Back To {{ shopifyData.blog.title }}</a
            >
          </div>
        </main>

        <!-- Sidebar -->
        <aside class="sidebar">
          <!-- Editor's Picks -->
          <div
            v-if="shopifyData.editorsPicks && shopifyData.editorsPicks.length > 0"
            class="sidebar-section"
          >
            <h3 class="sidebar-heading">EDITOR'S PICK</h3>
            <div class="editors-picks">
              <article
                v-for="article in shopifyData.editorsPicks"
                :key="article.url"
                class="pick-item"
              >
                <a :href="article.url" class="pick-link">
                  <div class="pick-image">
                    <img
                      :src="article.image"
                      :alt="article.title || 'Editor\'s Pick Image'"
                      loading="lazy"
                      aria-hidden="true"
                      aria-label="Editor's Pick Image"
                      width=""
                      height=""
                      @load="onImageLoad('article', article.title, article.image)"
                      @error="onImageError('article', article.title, article.image)"
                    />
                  </div>
                  <div class="pick-content">
                    <h4 class="pick-title">{{ article.title }}</h4>
                    <time class="pick-date">{{ article.date }}</time>
                    <p v-if="article.excerpt" class="pick-excerpt">
                      {{ decodeBase64(article.excerpt) }}
                    </p>
                  </div>
                </a>
              </article>
            </div>
          </div>

          <!-- Popular Tags -->
          <div
            v-if="shopifyData.popularTags && shopifyData.popularTags.length > 0"
            class="sidebar-section-tags"
          >
            <h3 class="sidebar-heading">POPULAR TAGS</h3>
            <div class="tags-container">
              <a
                v-for="tag in shopifyData.popularTags"
                :key="tag.name"
                :href="tag.url"
                class="tag-link"
              >
                {{ tag.name.toUpperCase() }}
              </a>
            </div>
          </div>

          <!-- Popular Products -->
          <div
            v-if="shopifyData.popularProducts && shopifyData.popularProducts.length > 0"
            class="sidebar-section-products"
          >
            <h3 class="sidebar-heading">POPULAR PRODUCTS</h3>
            <div class="popular-products">
              <article
                v-for="product in shopifyData.popularProducts"
                :key="product.url"
                class="product-item"
              >
                <a :href="product.url" class="product-link">
                  <div class="product-image">
                    <img
                      :src="product.image"
                      :alt="product.title"
                      loading="lazy"
                      aria-hidden="true"
                      aria-label="Product Image"
                      width=""
                      height=""
                      @load="onImageLoad('product', product.title, product.image)"
                      @error="onImageError('product', product.title, product.image)"
                    />
                  </div>
                  <div class="product-content">
                    <h4 class="product-title">{{ product.title.toUpperCase() }}</h4>
                    <span class="product-price">{{ product.price }}</span>
                  </div>
                </a>
              </article>
            </div>
            <a href="/collections/all-bags-wallets" class="see-all-link">SEE ALL ></a>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  // name: 'IndividualBlogPost',
  props: {
    shopifyData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    sectionStyles() {
      return {
        backgroundColor: this.shopifyData.bgColor || '#fff',
        paddingTop:
          window.innerWidth > 768
            ? (this.shopifyData.deskPaddingTop || '0') + 'px'
            : (this.shopifyData.mobPaddingTop || '0') + 'px',
        paddingBottom:
          window.innerWidth > 768
            ? (this.shopifyData.deskPaddingBottom || '60') + 'px'
            : (this.shopifyData.mobPaddingBottom || '40') + 'px',
      };
    },
    decodedContent() {
      if (!this.shopifyData.article.content) return '';
      try {
        return decodeURIComponent(escape(window.atob(this.shopifyData.article.content)));
      } catch (e) {
        return this.shopifyData.article.content;
      }
    },
    excerptParts() {
      if (!this.shopifyData.article.excerpt) return { first: '', rest: '' };
      try {
        const decoded = decodeURIComponent(escape(window.atob(this.shopifyData.article.excerpt)));
        // strip paragraph and line break tags
        const cleaned = decoded.replace(/<\/?p[^>]*>/gi, '').replace(/<br\s*\/?>/gi, '');
        const firstSentenceEnd = cleaned.indexOf('.') + 1;
        const first = cleaned.slice(0, firstSentenceEnd).trim();
        const rest = cleaned.slice(firstSentenceEnd).trim();
        return { first, rest };
      } catch (e) {
        return { first: this.shopifyData.article.excerpt, rest: '' };
      }
    },
  },
  mounted() {
    // Simple logging to confirm data is loaded
    // console.log(
    //   '✅ Individual blog post loaded with products:',
    //   this.shopifyData.popularProducts?.length || 0
    // );
    // console.log(
    //   '✅ Individual blog post loaded with articles:',
    //   this.shopifyData.editorsPicks?.length || 0
    // );
  },
  methods: {
    decodeBase64(str) {
      if (!str) return '';
      try {
        return decodeURIComponent(escape(window.atob(str)));
      } catch (e) {
        return str;
      }
    },
    onImageLoad(type, title, src) {
      console.log(`✅ ${type} image loaded successfully:`, {
        title,
        src,
      });
    },
    onImageError(type, title, src) {
      console.error(`❌ ${type} image failed to load:`, {
        title,
        src,
        error: 'Image load failed',
      });
    },
  },
};
</script>

<style>
.individual-blog-post {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  background-color: #ffffff;
  overflow-x: hidden;
}

/* Container */
.individual-blog-post .container {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 50px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.individual-blog-post .hero-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 50px;
  margin-bottom: 50px;
}

/* Blog Content Wrapper */
.individual-blog-post .blog-content-wrapper {
  width: 100%;
  overflow-x: hidden;
}

.individual-blog-post .content-container {
  margin-top: 80px;
}

.individual-blog-post .blog-layout {
  display: flex;
  gap: 80px;
  align-items: flex-start;
}

/* Article Header */
.individual-blog-post .article-header {
  display: flex;
  max-width: 1076px;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.individual-blog-post .article-title {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

.individual-blog-post .article-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.individual-blog-post .article-date {
  color: #000;
  font-family: 'Adobe Caslon Pro', Arial, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  letter-spacing: 0.28px;
}

.individual-blog-post .article-author {
  color: #666;
  text-align: center;
  font-family: 'Adobe Caslon Pro', Arial, serif;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.24px;
  display: none;
}

/* Hero Image - Full Width */
.individual-blog-post .hero-image-container {
  width: 100vw;
  height: 100%;
  position: relative;
  overflow: hidden;
  left: 50%;
  right: 50%;
  padding: 0 30px;
  margin-left: -50vw;
  margin-right: -50vw;
}

.individual-blog-post .hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

/* Main Content */
.individual-blog-post .main-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  padding: 0 20px 80px 20px;
  flex: 1;
}

/* Article Content */
.individual-blog-post .article-content {
  display: flex;
  max-width: 680px;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
}

.individual-blog-post .content-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.individual-blog-post .intro-text {
  color: #000;
  font-family: 'Adobe Caslon Pro', serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.2px;
  margin: 0;
  margin-bottom: 30px;
  width: 100%;
  max-width: 716px;
}

.individual-blog-post .intro-lead {
  display: block;
  text-align: start;
  font-family: 'Big Caslon', serif;
  font-size: 46px;
  font-weight: 500;
  line-height: 97%;
  margin: 31px auto 0px;
  width: 100%;
  max-width: 716px;
}

.individual-blog-post .section-heading {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
  margin-bottom: 20px;
}

.individual-blog-post .section-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.individual-blog-post .subsection-title {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  margin: 0;
}

.individual-blog-post .body-text {
  color: #000;
  font-family: 'Adobe Caslon Pro', Arial, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.2px;
  margin: 0;
}

.individual-blog-post .bullet-list {
  margin: 0;
  padding-left: 20px;
}

.individual-blog-post .bullet-list li {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.2px;
  margin-bottom: 8px;
}

.individual-blog-post .image-section {
  width: 100%;
  margin: 30px 0;
}

.individual-blog-post .content-image {
  width: 100%;
  height: 400px;
  object-fit: cover;
}

.individual-blog-post .article-body p span {
  color: #000;
  font-family: 'Adobe Caslon Pro', serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.28px;
}
.individual-blog-post .article-body p {
  color: #000;
  font-family: 'Adobe Caslon Pro', serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.28px;
  margin-bottom: 16px;
}

.individual-blog-post .article-body p a {
  color: #000;
  font-family: 'Adobe Caslon Pro', serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.28px;
  text-decoration: underline;
}

.individual-blog-post .article-body h1,
.individual-blog-post .article-body h2,
.individual-blog-post .article-body h3,
.individual-blog-post .article-body h4,
.individual-blog-post .article-body h5,
.individual-blog-post .article-body h6 {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  margin: 30px 0 20px 0;
}

.individual-blog-post .article-body h2 {
  font-size: 18px;
  line-height: 22px;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.individual-blog-post .article-body h3 {
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.8px;
}

.individual-blog-post .article-body h3 span {
  font-size: 16px;
  letter-spacing: 0.8px;
}

.individual-blog-post .article-body ul,
.individual-blog-post .article-body ol {
  margin: 16px 0;
  padding-left: 0;
  list-style: none !important;
}

.individual-blog-post .article-body ul li {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 22px;
  font-family: 'Adobe Caslon Pro', Arial, serif;
  color: #000;
  position: relative;
  padding-left: 24px;
}

.individual-blog-post .article-body ul li::before {
  content: '•';
  color: #000;
  font-size: 16px;
  position: absolute;
  left: 0;
  top: 0;
  line-height: 22px;
}

.individual-blog-post .article-body ol li {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 22px;
  font-family: 'Adobe Caslon Pro', Arial, serif;
  color: #000;
  position: relative;
  padding-left: 24px;
  counter-increment: list-counter;
}

.individual-blog-post .article-body ol {
  counter-reset: list-counter;
}

.individual-blog-post .article-body ol li::before {
  content: counter(list-counter) '.';
  color: #000;
  font-size: 14px;
  position: absolute;
  left: 0;
  top: 0;
  line-height: 22px;
}

.individual-blog-post .back-to-blog {
  margin-top: 60px;
  padding-top: 30px;
  border-top: 1px solid #e0e0e0;
}

.individual-blog-post .back-link {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-decoration: underline;
  text-decoration-skip-ink: none;
}

/* Sidebar */
.individual-blog-post .sidebar {
  display: flex;
  width: 414px;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  padding: 0 30px 0 0;
}

.individual-blog-post .sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.individual-blog-post .sidebar-heading {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
}

.individual-blog-post .sidebar-section-tags {
  display: flex;
  height: 202px;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  align-self: stretch;
}

.individual-blog-post .sidebar-section-products {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  align-self: stretch;
}

/* Editor's Picks */
.individual-blog-post .editors-picks {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  align-self: stretch;
}

.individual-blog-post .pick-item {
  display: flex;
  gap: 18px;
  align-items: flex-start;
}

.individual-blog-post .pick-link {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  width: 100%;
  text-decoration: none;
  color: inherit;
}

.individual-blog-post .pick-image {
  width: 150px;
  height: 150px;
  flex-shrink: 0;
  background-color: #f0f0f0;
}

.individual-blog-post .pick-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.individual-blog-post .pick-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.individual-blog-post .pick-title {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin: 0;
}

.individual-blog-post .pick-date {
  color: #666;
  font-family: 'Adobe Caslon Pro', Arial, serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.3px;
}

.individual-blog-post .pick-excerpt {
  color: #666;
  font-family: 'Adobe Caslon Pro', Arial, serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  letter-spacing: 0.2px;
  margin: 0;
  margin-top: 4px;
}

/* Popular Tags */
.individual-blog-post .tags-container {
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 20px;
  align-self: stretch;
  flex-wrap: wrap;
}

.individual-blog-post .tag-link {
  background-color: #f0f0f0;
  padding: 8px 12px;
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-decoration: none;
  transition: background-color 0.3s ease;
}

.individual-blog-post .tag-link:hover {
  background-color: #e0e0e0;
}

/* Popular Products */
.individual-blog-post .popular-products {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.individual-blog-post .product-item {
  display: flex;
  align-items: center;
  gap: 18px;
  align-self: stretch;
}

.individual-blog-post .product-link {
  display: flex;
  gap: 18px;
  align-items: center;
  width: 100%;
  text-decoration: none;
  color: inherit;
}

.individual-blog-post .product-image {
  width: 150px;
  height: 150px;
  flex-shrink: 0;
  background-color: #f0f0f0;
}

.individual-blog-post .product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.individual-blog-post .product-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}

.individual-blog-post .product-title {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  margin: 0;
}

.individual-blog-post .product-price {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 12px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.individual-blog-post .see-all-link {
  color: #000;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-decoration: none;
  margin-top: 20px;
}

.individual-blog-post .see-all-link:hover {
  text-decoration: underline;
}
/* Responsive Design */
@media (max-width: 1200px) {
  .individual-blog-post .container {
    max-width: 100%;
    padding: 0 30px;
  }

  .individual-blog-post .blog-layout {
    gap: 60px;
  }

  .individual-blog-post .sidebar {
    width: 350px;
  }
}

@media (max-width: 1024px) {
  .individual-blog-post .container {
    padding: 0 20px;
  }

  .individual-blog-post .blog-layout {
    flex-direction: column;
    gap: 50px;
  }

  .individual-blog-post .sidebar {
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
  }

  .individual-blog-post .main-content {
    max-width: 100%;
    padding: 0 0 60px 0;
  }

  .individual-blog-post .article-content {
    max-width: 100%;
  }

  .individual-blog-post .hero-image-container {
    padding: 0 30px;
    height: 100%;
  }

  .individual-blog-post .content-container {
    margin-top: 60px;
  }
}

@media (max-width: 768px) {
  .individual-blog-post .container {
    padding: 0 15px;
    gap: 30px;
  }

  .individual-blog-post .hero-section {
    margin-top: 30px;
    margin-bottom: 30px;
  }

  .individual-blog-post .article-title {
    font-size: 15px;
    line-height: 36px;
  }

  .individual-blog-post .hero-image-container {
    padding: 0 30px;
    height: 100%;
  }

  .individual-blog-post .content-container {
    margin-top: 40px;
  }

  .individual-blog-post .blog-layout {
    gap: 40px;
  }

  .individual-blog-post .main-content {
    padding: 0 0 40px 0;
  }

  .individual-blog-post .article-content {
    gap: 25px;
  }

  .individual-blog-post .content-section {
    gap: 14px;
  }

  .individual-blog-post .intro-lead {
    font-size: 26px;
    margin: 10px auto 0px;
  }

  .individual-blog-post .intro-text {
    font-size: 15px;
    line-height: 22px;
  }

  .individual-blog-post .article-body {
    font-size: 15px;
    line-height: 22px;
  }

  .individual-blog-post .article-body h2 {
    font-size: 16px;
    line-height: 20px;
    margin: 25px 0 15px 0;
  }

  .individual-blog-post .article-body h3 {
    font-size: 15px;
    line-height: 18px;
    margin: 20px 0 12px 0;
  }

  .individual-blog-post .sidebar {
    gap: 40px;
  }

  .individual-blog-post .sidebar-section,
  .sidebar-section-tags,
  .sidebar-section-products {
    gap: 25px;
  }

  .individual-blog-post .pick-item,
  .product-item {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }

  .individual-blog-post .pick-image,
  .product-image {
    width: 100%;
    height: 200px;
    max-width: 300px;
    margin: 0 auto;
  }

  .individual-blog-post .pick-content,
  .product-content {
    width: 100%;
  }

  .individual-blog-post .tags-container {
    gap: 15px;
  }

  .individual-blog-post .tag-link {
    padding: 6px 10px;
    font-size: 9px;
  }
}

@media (max-width: 480px) {
  .individual-blog-post .container {
    padding: 0 15px;
    gap: 25px;
  }

  .individual-blog-post .hero-section {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .individual-blog-post .intro-lead {
    font-size: 26px;
    margin: 10px auto 0px;
  }

  .individual-blog-post .article-header {
    gap: 8px;
  }

  .individual-blog-post .article-title {
    font-size: 15px;
    line-height: 32px;
  }

  .individual-blog-post .article-date {
    font-size: 13px;
  }

  .individual-blog-post .article-author {
    font-size: 11px;
  }

  .individual-blog-post .hero-image-container {
    padding: 0 30px;
    height: 100%;
  }

  .individual-blog-post .content-container {
    margin-top: 30px;
  }

  .individual-blog-post .blog-layout {
    gap: 30px;
  }

  .individual-blog-post .main-content {
    padding: 0 0 30px 0;
  }

  .individual-blog-post .article-content {
    gap: 20px;
  }

  .individual-blog-post .content-section {
    gap: 14px;
  }

  .individual-blog-post .intro-text {
    font-size: 14px;
    line-height: 20px;
  }

  .individual-blog-post .article-body {
    font-size: 14px;
    line-height: 20px;
  }

  .individual-blog-post .article-body h2 {
    font-size: 15px;
    line-height: 18px;
    margin: 20px 0 12px 0;
  }

  .individual-blog-post .article-body h3 {
    font-size: 14px;
    line-height: 16px;
    margin: 16px 0 10px 0;
  }

  .individual-blog-post .article-body ul li,
  .individual-blog-post .article-body ol li {
    font-size: 13px;
    line-height: 18px;
    padding-left: 20px;
  }

  .individual-blog-post .back-to-blog {
    margin-top: 40px;
    padding-top: 20px;
  }

  .individual-blog-post .back-link {
    font-size: 11px;
  }

  .individual-blog-post .sidebar {
    gap: 30px;
  }

  .individual-blog-post .sidebar-section,
  .sidebar-section-tags,
  .sidebar-section-products {
    gap: 20px;
  }

  .individual-blog-post .sidebar-heading {
    font-size: 11px;
  }

  .individual-blog-post .pick-item,
  .product-item {
    gap: 12px;
  }

  .individual-blog-post .pick-image,
  .product-image {
    height: 180px;
    max-width: 250px;
  }

  .individual-blog-post .pick-title {
    font-size: 10px;
    line-height: 12px;
  }

  .individual-blog-post .pick-date {
    font-size: 9px;
  }

  .individual-blog-post .pick-excerpt {
    font-size: 9px;
    line-height: 12px;
  }

  .individual-blog-post .product-title {
    font-size: 9px;
    line-height: 11px;
  }

  .individual-blog-post .product-price {
    font-size: 9px;
  }

  .individual-blog-post .tags-container {
    gap: 12px;
  }

  .individual-blog-post .tag-link {
    padding: 5px 8px;
    font-size: 8px;
  }

  .individual-blog-post .see-all-link {
    font-size: 9px;
    margin-top: 15px;
  }
}
</style>
